/**
 * The offline queue for set saves (PUT /update_actual_sets).
 *
 * WHAT IS QUEUED. That endpoint takes the COMPLETE logged state of one
 * prescribed set (see components/group_results/payload.js), so a newer save for
 * the same set supersedes an older one. Rather than replay every keystroke's
 * autosave, a new entry REPLACES the newest queued entry for that set when it
 * is the same kind of request. A skip toggle ({ skipped } with no
 * actual_sets_data) is a different kind: skipping destroys the rows, so it does
 * not commute with a data save and is appended instead, keeping the real order.
 *
 * ORDER. Entries replay strictly in queue order, one at a time. While a set has
 * anything queued, the interceptor routes NEW saves for that set into the queue
 * too (see hasPendingFor), even when online, so a stale replay can never land
 * on top of a fresher live save.
 *
 * NO DUPLICATES. Every entry carries an Idempotency-Key minted when it was
 * first queued. The server (IdempotentRequest) answers a key it has already
 * processed from its record, so a save that committed just before the signal
 * died is not run a second time when it is replayed.
 *
 * ACCOUNT. Each entry records the user it was logged by and only replays while
 * that same user is signed in on this device. A save logged by one account must
 * never be sent under another's session.
 */

import { REQUESTS_STORE, deleteRecord, getAll, transact } from "./offline_db";

export const SET_SAVE_URL = "/update_actual_sets";

// Entries are dropped (and reported) after this many hard failures. A 4xx is
// permanent and gives up at once; this bounds a server that keeps 5xx-ing.
export const MAX_ATTEMPTS = 8;

export const STATUS_PENDING = "pending";
export const STATUS_FAILED = "failed";

export const mintIdempotencyKey = (prefix = "set") => {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${uuid}`;
};

const currentUserId = () =>
  typeof window !== "undefined" && window._tkcCurrentUserId != null
    ? String(window._tkcCurrentUserId)
    : null;

export const isSetSaveRequest = (config) => {
  if (!config || String(config.method || "").toLowerCase() !== "put") {
    return false;
  }
  const url = String(config.url || "");
  // Relative (the callers' form) or absolute on this origin.
  const path = url.startsWith("http") ? new URL(url).pathname : url;
  return path.split("?")[0] === SET_SAVE_URL;
};

const kindOf = (data) =>
  data && Object.prototype.hasOwnProperty.call(data, "actual_sets_data")
    ? "data"
    : "skip";

// In-memory mirror of which sets have queued work, so the axios request
// interceptor can decide synchronously. Rebuilt from IndexedDB on load.
const pendingSets = new Set();
const setKey = (assignedSetId) => String(assignedSetId);

export const hasPendingFor = (data) =>
  data?.assigned_set_id != null &&
  pendingSets.has(setKey(data.assigned_set_id));

export const hasAnyPending = () => pendingSets.size > 0;

const rebuildMirror = (records) => {
  pendingSets.clear();
  records
    .filter((record) => record.status === STATUS_PENDING)
    .forEach((record) => pendingSets.add(setKey(record.assignedSetId)));
};

/**
 * Queue one set save. Resolves the stored entry. `idempotencyKey` is the key
 * the original request already carried, when there was one: a request that
 * reached the network and lost its reply must replay under the SAME key, or the
 * server cannot recognise it.
 */
export async function enqueueSetSave(data, { idempotencyKey } = {}) {
  const userId = currentUserId();
  if (!userId || data?.assigned_set_id == null) {
    throw new Error("Cannot queue a set save without a user and a set");
  }
  const assignedSetId = setKey(data.assigned_set_id);
  const kind = kindOf(data);

  const entry = await transact(REQUESTS_STORE, (records) => {
    const sameSet = records
      .filter(
        (record) =>
          record.assignedSetId === assignedSetId &&
          record.userId === userId &&
          record.status === STATUS_PENDING,
      )
      .sort((a, b) => a.seq - b.seq);
    const newest = sameSet[sameSet.length - 1];
    const maxSeq = records.reduce(
      (max, record) => Math.max(max, record.seq),
      0,
    );

    if (newest && newest.kind === kind) {
      // Supersede in place: the queue keeps its order, the payload is the
      // newest full state, and the key is fresh because the body changed (the
      // server refuses a key reused for a different body). The superseded
      // key may already have committed; this full-state save overwrites it.
      const replaced = {
        ...newest,
        data,
        idempotencyKey: idempotencyKey || mintIdempotencyKey(),
        updatedAt: Date.now(),
        attempts: 0,
        lastError: null,
      };
      return { put: [replaced], result: replaced };
    }

    const created = {
      id: mintIdempotencyKey("entry"),
      seq: maxSeq + 1,
      kind,
      assignedSetId,
      userId,
      url: SET_SAVE_URL,
      method: "put",
      data,
      idempotencyKey: idempotencyKey || mintIdempotencyKey(),
      status: STATUS_PENDING,
      attempts: 0,
      lastError: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    return { put: [created], result: created };
  });

  pendingSets.add(assignedSetId);
  return entry;
}

/** All entries, oldest first. Includes other users' (for the counts only). */
export async function loadSetSaves() {
  const records = await getAll(REQUESTS_STORE);
  records.sort((a, b) => a.seq - b.seq);
  rebuildMirror(records);
  return records;
}

export async function removeSetSave(entry) {
  // Delete only the version that was sent. If a newer save superseded it while
  // it was in flight, that newer payload (same id, new key) must stay queued.
  const removed = await transact(REQUESTS_STORE, (records) => {
    const current = records.find((record) => record.id === entry.id);
    if (!current || current.idempotencyKey !== entry.idempotencyKey) {
      return { result: false };
    }
    return { remove: [entry.id], result: true };
  });
  await loadSetSaves();
  return removed;
}

export async function markSetSave(entry, patch) {
  await transact(REQUESTS_STORE, (records) => {
    const current = records.find((record) => record.id === entry.id);
    if (!current || current.idempotencyKey !== entry.idempotencyKey) {
      return {};
    }
    return { put: [{ ...current, ...patch, updatedAt: Date.now() }] };
  });
  await loadSetSaves();
}

export async function discardSetSave(id) {
  await deleteRecord(REQUESTS_STORE, id);
  await loadSetSaves();
}

/** What the caller's .then() sees for a queued save. */
export const queuedSetSaveResponse = (config, data) => {
  const rows = Array.isArray(data?.actual_sets_data)
    ? data.actual_sets_data
    : [];
  return {
    // Same shape as the real 200 so every caller's .then() works unchanged.
    // dropped_rows is empty because nothing was refused (nothing has run yet);
    // logged_count is what WILL be written, so reportDroppedActualSets stays
    // quiet instead of paging "persisted nothing" for every offline save.
    data: {
      dropped_rows: [],
      logged_count: rows.filter((row) => !row?.skipped).length,
      queued: true,
    },
    status: 202,
    statusText: "Queued offline",
    headers: {},
    config,
    request: null,
  };
};

export const resetSetSaveQueueForTests = () => {
  pendingSets.clear();
};
